import { Injectable, inject, signal } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, timer } from 'rxjs';
import { CampaignsService, BulkRunSnapshot } from '@features/campaigns/services/campaigns.service';
import { AnalysisService } from '@shared/services/analysis.service';
import { NotificationStore } from '@shared/stores/notification.store';
import { AnalysisCacheService } from '@features/analyses/store/services/analysis-cache.service';
import { Analysis } from '@shared/models/analysis.model';
import {
  BulkJobState,
  PersistedBulkJob,
  PersistedWatchedAnalysis,
  WatchedAnalysis,
  BULK_JOB_TTL_MS,
  WATCHED_ANALYSIS_TTL_MS,
  LS_KEY_BULK,
  LS_KEY_WATCHED,
} from '@core/models/background-execution.model';

interface BulkSseState {
  sub: Subscription | null;
  reconnectAttempts: number;
  safetyTimer: ReturnType<typeof setTimeout> | null;
}

const MAX_BULK_RECONNECT = 3;

@Injectable({ providedIn: 'root' })
export class BackgroundExecutionService {
  private readonly campaignsService = inject(CampaignsService);
  private readonly analysisService = inject(AnalysisService);
  private readonly notificationStore = inject(NotificationStore);
  private readonly cacheService = inject(AnalysisCacheService);

  // ─── Bulk job state (execution-panel) ─────────────────────────────────────

  private readonly _bulkJobs = signal<Map<string, BulkJobState>>(new Map());
  /** Read-only signal keyed by campaignId — execution-panel subscribes to this. */
  readonly bulkJobs = this._bulkJobs.asReadonly();

  private readonly _bulkSse = new Map<string, BulkSseState>();

  // ─── Watched analysis state (analysis-detail pipeline) ────────────────────

  // analysisId → teardown Subject for the background SSE
  private readonly _analysisDone$ = new Map<string, Subject<void>>();
  // analysisId → registered metadata pending handoff from the detail page
  private readonly _watchedPending = new Map<string, WatchedAnalysis>();

  // ─── Init ──────────────────────────────────────────────────────────────────

  constructor() {
    this._restoreFromStorage();
  }

  // ─── Public API: bulk jobs ─────────────────────────────────────────────────

  /**
   * Called by execution-panel immediately after createBulkRun() returns a snapshot.
   * Takes over SSE management and notification lifecycle for this run.
   */
  registerBulkRun(params: {
    organizationId: string;
    campaignId: string;
    orgSlug: string | null;
    snapshot: BulkRunSnapshot;
  }): void {
    const { organizationId, campaignId, orgSlug, snapshot } = params;
    const startedAt = snapshot.startedAt ?? new Date().toISOString();

    const routerLink = orgSlug ? `/organizations/${orgSlug}/campaigns/${campaignId}` : undefined;
    const notifId = this.notificationStore.addProgress(
      `Ausführung gestartet (${snapshot.total} Analyse${snapshot.total === 1 ? '' : 'n'})`,
      routerLink,
    );

    this._setBulkJob(campaignId, {
      runId: snapshot.runId,
      organizationId,
      campaignId,
      phase: 'running',
      total: snapshot.total,
      dispatched: snapshot.dispatched,
      succeeded: snapshot.succeeded,
      failed: snapshot.failed,
      skipped: snapshot.skipped,
      startedAt,
      completed: snapshot.completed,
      pipelineFailed: snapshot.pipelineFailed,
      processing: snapshot.processing,
      queued: snapshot.queued,
      analysisUpdates: snapshot.analysisUpdates,
      notifId,
    });

    this._persistBulkJob({ runId: snapshot.runId, organizationId, campaignId, orgSlug, startedAt });
    this._openBulkSse(campaignId, organizationId, campaignId, snapshot.runId);
  }

  /**
   * Called by execution-panel.reset() — cancels the running job and cleans up.
   */
  cancelBulkJob(campaignId: string): void {
    this._closeBulkSse(campaignId);
    const job = this._bulkJobs().get(campaignId);
    if (job) {
      this.notificationStore.remove(job.notifId);
    }
    this._removeBulkJob(campaignId);
    this._removeBulkFromStorage(campaignId);
  }

  // ─── Public API: watched analyses ─────────────────────────────────────────

  /**
   * Called by AnalysisWorkflowFacade after triggering a pipeline action.
   * Registers the analysis for background notification — SSE opens only after
   * handOffAnalysisToBackground() is called (i.e. when the user leaves the page).
   */
  watchAnalysis(params: {
    analysisId: string;
    organizationId: string;
    orgSlug: string | null;
    taskWindow?: { startTask: number; endTask: number };
  }): void {
    const { analysisId, organizationId, orgSlug, taskWindow } = params;

    // Create notification immediately so users see it in the bell
    const notifId = this.notificationStore.addProgress(
      'Analyse wird ausgeführt…',
      orgSlug ? `/organizations/${orgSlug}/analyses/${analysisId}` : undefined,
    );

    const entry: WatchedAnalysis = {
      analysisId,
      organizationId,
      orgSlug,
      notifId,
      startedAt: new Date().toISOString(),
      ...taskWindow,
    };
    this._watchedPending.set(analysisId, entry);
    this._persistWatchedAnalysis(entry);
  }

  taskWindowForAnalysis(analysisId: string): { startTask: number; endTask: number } | null {
    const entry = this._watchedPending.get(analysisId);
    return Number.isFinite(entry?.startTask) && Number.isFinite(entry?.endTask)
      ? { startTask: entry!.startTask!, endTask: entry!.endTask! }
      : null;
  }

  /**
   * Called by analysis-detail.ngOnDestroy(). Opens the background SSE for this
   * analysis so status updates continue after the user leaves the page.
   * No-op if no pending registration exists or a connection is already open.
   */
  handOffAnalysisToBackground(analysisId: string): void {
    if (!analysisId) return;
    const entry = this._watchedPending.get(analysisId);
    if (!entry) return; // not registered — ignore
    this._openBackgroundAnalysisSse(entry);
  }

  /** Re-attach to a persisted run after a page reload: hydrate counters, then resume SSE. */
  private async _reattachBulkRun(entry: PersistedBulkJob): Promise<void> {
    let snapshot: BulkRunSnapshot;
    try {
      snapshot = await this.campaignsService.getExecutionProgress(
        entry.organizationId,
        entry.campaignId,
      ) ?? await this.campaignsService.getBulkRun(
          entry.organizationId,
          entry.campaignId,
          entry.runId,
        );
    } catch {
      this._removeBulkFromStorage(entry.campaignId);
      return;
    }

    const isTerminal =
      snapshot.status === 'completed' ||
      snapshot.status === 'failed' ||
      snapshot.status === 'cancelled';

    const notifId = this.notificationStore.addProgress(
      isTerminal ? 'Ausführung abgeschlossen' : 'Verbinde mit laufender Ausführung…',
      entry.orgSlug ? `/organizations/${entry.orgSlug}/campaigns/${entry.campaignId}` : undefined,
    );

    this._setBulkJob(entry.campaignId, {
      runId: snapshot.runId,
      organizationId: entry.organizationId,
      campaignId: entry.campaignId,
      phase: isTerminal ? (snapshot.status === 'failed' ? 'failed' : 'done') : 'running',
      total: snapshot.total,
      dispatched: snapshot.dispatched,
      succeeded: snapshot.succeeded,
      failed: snapshot.failed,
      skipped: snapshot.skipped,
      startedAt: snapshot.startedAt ?? entry.startedAt,
      completed: snapshot.completed,
      pipelineFailed: snapshot.pipelineFailed,
      processing: snapshot.processing,
      queued: snapshot.queued,
      analysisUpdates: snapshot.analysisUpdates,
      notifId,
    });

    if (isTerminal) {
      this._finalizeBulkJob(entry.campaignId, snapshot.status === 'failed' ? 'failed' : 'done');
    } else {
      this._openBulkSse(entry.campaignId, entry.organizationId, entry.campaignId, snapshot.runId);
    }
  }

  // ─── Private: bulk SSE ────────────────────────────────────────────────────

  private _openBulkSse(key: string, organizationId: string, campaignId: string, runId: string): void {
    this._closeBulkSse(key);

    const state: BulkSseState = { sub: null, reconnectAttempts: 0, safetyTimer: null };
    this._bulkSse.set(key, state);

    const connect = () => {
      state.sub = this.campaignsService
        .watchBulkRunEvents(organizationId, campaignId, runId)
        .subscribe({
          next: (event: any) => {
            state.reconnectAttempts = 0;
            this._resetBulkSafetyTimer(key, 180_000);
            this._handleBulkSseEvent(campaignId, event);
          },
          error: () => {
            this._clearBulkSafetyTimer(key);
            if (state.reconnectAttempts < MAX_BULK_RECONNECT) {
              state.reconnectAttempts++;
              const delay = 2000 * state.reconnectAttempts;
              state.safetyTimer = setTimeout(() => { if (this._bulkSse.get(key) === state) connect(); }, delay);
            } else {
              const job = this._bulkJobs().get(campaignId);
              if (job) this.notificationStore.updateProgress(job.notifId, {body: 'Verbindung unterbrochen. Der Status wird erneut geprüft.'});
              state.safetyTimer = setTimeout(() => { if (this._bulkSse.get(key) === state) connect(); }, 30_000);
            }
          },
          complete: () => {
            this._clearBulkSafetyTimer(key);
            const job = this._bulkJobs().get(campaignId);
            if (job?.phase === 'running') {
              state.safetyTimer = setTimeout(() => { if (this._bulkSse.get(key) === state) connect(); }, 2_000);
            }
          },
        });
    };

    this._resetBulkSafetyTimer(key, 600_000);
    connect();
  }

  private _closeBulkSse(key: string): void {
    const state = this._bulkSse.get(key);
    if (!state) return;
    this._clearBulkSafetyTimer(key);
    state.sub?.unsubscribe();
    state.sub = null;
    this._bulkSse.delete(key);
  }

  private _resetBulkSafetyTimer(key: string, ms: number): void {
    this._clearBulkSafetyTimer(key);
    const state = this._bulkSse.get(key);
    if (!state) return;
    state.safetyTimer = setTimeout(() => {
      const job = this._bulkJobs().get(key);
      if (job?.phase === 'running') this.notificationStore.updateProgress(job.notifId, {body: 'Warte auf aktuelle Statusdaten. Die Ausführung läuft im Hintergrund weiter.'});
    }, ms);
  }

  private _clearBulkSafetyTimer(key: string): void {
    const state = this._bulkSse.get(key);
    if (!state) return;
    if (state.safetyTimer !== null) {
      clearTimeout(state.safetyTimer);
      state.safetyTimer = null;
    }
  }

  private _handleBulkSseEvent(campaignId: string, event: any): void {
    const job = this._bulkJobs().get(campaignId);
    if (!job) return;

    if (event.type === 'run_progress' || event.type === 'run_complete') {
      this._updateBulkJob(campaignId, {
        total: event.total,
        dispatched: event.dispatched,
        succeeded: event.succeeded,
        failed: event.failed,
        skipped: event.skipped,
        ...(event.startedAt ? { startedAt: event.startedAt } : {}),
        ...(event.completed !== undefined ? { completed: event.completed } : {}),
        ...(event.pipelineFailed !== undefined ? { pipelineFailed: event.pipelineFailed } : {}),
        ...(event.processing !== undefined ? { processing: event.processing } : {}),
        ...(event.queued !== undefined ? { queued: event.queued } : {}),
        ...(event.analysisUpdates ? { analysisUpdates: event.analysisUpdates } : {}),
      });
      const planned = event.total - event.skipped;
      if (event.type === 'run_progress' && planned > 0) {
        const done = event.succeeded + event.failed;
        this.notificationStore.updateProgress(job.notifId, {
          progress: Math.round((done / planned) * 100),
          body: `${done} von ${planned} Analysen${event.failed ? ` · ${event.failed} fehlgeschlagen` : ''}`,
        });
      }
      if (event.type === 'run_complete') {
        this._clearBulkSafetyTimer(campaignId);
        this._finalizeBulkJob(campaignId, event.status === 'failed' ? 'failed' : 'done');
      }
    }
  }

  private _finalizeBulkJob(campaignId: string, outcome: 'done' | 'failed'): void {
    this._closeBulkSse(campaignId);

    const job = this._bulkJobs().get(campaignId);
    if (!job) return;

    const dispatched = job.dispatched;
    const failed = job.failed;
    const skipped = job.skipped;

    this.notificationStore.updateProgress(job.notifId, {
      status: outcome === 'done' ? 'complete' : 'failed',
      body: outcome === 'done'
        ? `${dispatched} gestartet${skipped ? `, ${skipped} übersprungen` : ''}${failed ? `, ${failed} fehlgeschlagen` : ''}`
        : 'Ausführung fehlgeschlagen',
    });

    // Invalidate all analysis caches so stale data is not served on next navigation
    this.cacheService.invalidateAll();

    this._updateBulkJob(campaignId, { phase: outcome });
    this._removeBulkFromStorage(campaignId);
  }

  // ─── Private: background analysis SSE ────────────────────────────────────

  private _openBackgroundAnalysisSse(entry: WatchedAnalysis): void {
    if (this._analysisDone$.has(entry.analysisId)) return; // already open

    const done$ = new Subject<void>();
    this._analysisDone$.set(entry.analysisId, done$);

    const elapsedMs = Date.now() - new Date(entry.startedAt).getTime();
    const remainingMs = Math.max(WATCHED_ANALYSIS_TTL_MS - elapsedMs, 60_000);

    this.analysisService
      .streamAnalysisEvents(entry.organizationId, entry.analysisId)
      .pipe(
        takeUntil(done$),
        takeUntil(timer(remainingMs)),
      )
      .subscribe({
        next: (analysis: Analysis) => {
          const state = analysis.technicalStatus?.state;
          if (state === 'completed' || state === 'failed') {
            this._finalizeWatchedAnalysis(entry, state === 'completed' ? 'done' : 'failed', analysis);
          }
        },
        error: () => this._removeWatchedAnalysis(entry.analysisId),
        complete: () => this._removeWatchedAnalysis(entry.analysisId),
      });
  }

  private _finalizeWatchedAnalysis(entry: WatchedAnalysis, outcome: 'done' | 'failed', analysis?: Analysis): void {
    this.notificationStore.updateProgress(entry.notifId, {
      status: outcome === 'done' ? 'complete' : 'failed',
      body: outcome === 'done' ? 'Analyse abgeschlossen' : 'Analyse fehlgeschlagen',
      ...(outcome === 'failed' && analysis ? {
        errorCode: analysis.technicalErrorCode ?? analysis.technicalStatus?.errorCode,
        errorMessage: analysis.technicalErrorMessage ?? analysis.technicalStatus?.errorMessage,
        errorDetail: analysis.technicalErrorDetail ?? analysis.technicalStatus?.errorDetail,
      } : {}),
    });
    this.cacheService.invalidateAll();
    this._removeWatchedAnalysis(entry.analysisId);
  }

  private _removeWatchedAnalysis(analysisId: string): void {
    const done$ = this._analysisDone$.get(analysisId);
    if (done$) {
      done$.next();
      done$.complete();
      this._analysisDone$.delete(analysisId);
    }
    this._watchedPending.delete(analysisId);
    this._removeWatchedFromStorage(analysisId);
  }

  // ─── Private: restore from storage ────────────────────────────────────────

  private _restoreFromStorage(): void {
    const now = Date.now();

    // Bulk runs — hydrate counters from the durable run, then re-attach the SSE.
    const bulkEntries = this._loadBulkJobs();
    for (const entry of bulkEntries) {
      const age = now - new Date(entry.startedAt).getTime();
      if (age > BULK_JOB_TTL_MS) {
        this._removeBulkFromStorage(entry.campaignId);
        continue;
      }
      void this._reattachBulkRun(entry);
    }

    // Watched analyses
    const watchedEntries = this._loadWatchedAnalyses();
    for (const entry of watchedEntries) {
      const age = now - new Date(entry.startedAt).getTime();
      if (age > WATCHED_ANALYSIS_TTL_MS) continue;

      const watchedEntry: WatchedAnalysis = {
        analysisId: entry.analysisId,
        organizationId: entry.organizationId,
        orgSlug: entry.orgSlug,
        notifId: entry.notifId,
        startedAt: entry.startedAt,
        startTask: entry.startTask,
        endTask: entry.endTask,
      };
      this._watchedPending.set(entry.analysisId, watchedEntry);
      // On reload we open immediately — no inline SSE conflict
      this._openBackgroundAnalysisSse(watchedEntry);
    }
  }

  // ─── Private: signal helpers ──────────────────────────────────────────────

  private _setBulkJob(campaignId: string, state: BulkJobState): void {
    this._bulkJobs.update((m) => {
      const next = new Map(m);
      next.set(campaignId, state);
      return next;
    });
  }

  private _updateBulkJob(campaignId: string, patch: Partial<BulkJobState>): void {
    this._bulkJobs.update((m) => {
      const existing = m.get(campaignId);
      if (!existing) return m;
      const next = new Map(m);
      next.set(campaignId, { ...existing, ...patch });
      return next;
    });
  }

  private _removeBulkJob(campaignId: string): void {
    this._bulkJobs.update((m) => {
      const next = new Map(m);
      next.delete(campaignId);
      return next;
    });
  }

  // ─── Private: localStorage ────────────────────────────────────────────────

  private _persistBulkJob(entry: PersistedBulkJob): void {
    try {
      const existing = this._loadBulkJobs().filter((e) => e.campaignId !== entry.campaignId);
      localStorage.setItem(LS_KEY_BULK, JSON.stringify([...existing, entry]));
    } catch { /* storage unavailable */ }
  }

  private _removeBulkFromStorage(campaignId: string): void {
    try {
      const updated = this._loadBulkJobs().filter((e) => e.campaignId !== campaignId);
      localStorage.setItem(LS_KEY_BULK, JSON.stringify(updated));
    } catch { /* storage unavailable */ }
  }

  private _loadBulkJobs(): PersistedBulkJob[] {
    try {
      const raw = localStorage.getItem(LS_KEY_BULK);
      return raw ? (JSON.parse(raw) as PersistedBulkJob[]) : [];
    } catch { return []; }
  }

  private _persistWatchedAnalysis(entry: WatchedAnalysis): void {
    try {
      const existing = this._loadWatchedAnalyses().filter((e) => e.analysisId !== entry.analysisId);
      const persisted: PersistedWatchedAnalysis = {
        analysisId: entry.analysisId,
        organizationId: entry.organizationId,
        orgSlug: entry.orgSlug,
        notifId: entry.notifId,
        startedAt: entry.startedAt,
        startTask: entry.startTask,
        endTask: entry.endTask,
      };
      localStorage.setItem(LS_KEY_WATCHED, JSON.stringify([...existing, persisted]));
    } catch { /* storage unavailable */ }
  }

  private _removeWatchedFromStorage(analysisId: string): void {
    try {
      const updated = this._loadWatchedAnalyses().filter((e) => e.analysisId !== analysisId);
      localStorage.setItem(LS_KEY_WATCHED, JSON.stringify(updated));
    } catch { /* storage unavailable */ }
  }

  private _loadWatchedAnalyses(): PersistedWatchedAnalysis[] {
    try {
      const raw = localStorage.getItem(LS_KEY_WATCHED);
      return raw ? (JSON.parse(raw) as PersistedWatchedAnalysis[]) : [];
    } catch { return []; }
  }
}
